import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { enqueue } from "@/lib/offlineQueue";
import OfflineDataBanner from "@/components/OfflineDataBanner";
import { compressFile } from "@/lib/imageCompress";
import { PageHeader, StatusBadge, Progress, nomeItem } from "@/components/pcp";
import { Camera, CheckCircle, X, Warning, Package, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Mobile-first apontamento

// yyyy-mm-dd de HOJE no timezone local (para input[type=date] e default).
const todayISODate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Combina data yyyy-mm-dd escolhida com a hora ATUAL local e retorna ISO string.
// Se a data for hoje: fica igual a new Date().toISOString().
// Se retroativa: preserva a hora atual (útil pra ordenação) na data escolhida.
const dateWithNowToIso = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return new Date().toISOString();
  const now = new Date();
  const [y, m, d] = yyyy_mm_dd.split("-").map(Number);
  const combined = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  return combined.toISOString();
};

export default function Apontamento() {
  const { user } = useAuth();
  const [ordens, setOrdens] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [selectedOs, setSelectedOs] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedEquipe, setSelectedEquipe] = useState("");
  const [dataExecucao, setDataExecucao] = useState(todayISODate());
  const [quantidade, setQuantidade] = useState("");
  const [extras, setExtras] = useState([]); // itens adicionais no mesmo lançamento: [{item_id, quantidade}] (ex.: terminal/aba junto da defensa)
  const [observacao, setObservacao] = useState("");
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itens, setItens] = useState([]);
  const [composicoes, setComposicoes] = useState([]);
  const [enviouMaterialExtra, setEnviouMaterialExtra] = useState(null); // null=nao perguntou, true/false
  const [materialExtra, setMaterialExtra] = useState([]); // [{insumo_id, insumo_nome, unidade, quantidade}]
  const [geo, setGeo] = useState(null); // {latitude, longitude, accuracy}

  const [coordTxt, setCoordTxt] = useState("");
  // Coordenada digitada/colada (opcional): "-16.123456, -49.654321" ou "-16,123456 -49,654321"
  const parseCoord = (txt) => {
    const m = String(txt).trim().match(/^(-?\d{1,2}(?:[.,]\d+)?)\s*[,; ]\s*(-?\d{1,3}(?:[.,]\d+)?)$/);
    if (!m) return null;
    const lat = parseFloat(m[1].replace(",", ".")), lng = parseFloat(m[2].replace(",", "."));
    return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { latitude: lat, longitude: lng, accuracy: null, manual: true } : null;
  };
  const onCoord = (txt) => { setCoordTxt(txt); setGeo(txt.trim() ? parseCoord(txt) : null); };

  useEffect(() => {
    api.get("/ordens").then((r) => setOrdens(r.data.filter((o) => o.status !== "Concluída" && o.status !== "Cancelada")));
    api.get("/equipes").then((r) => setEquipes(r.data));
    api.get("/composicoes").then((r) => setComposicoes(r.data));
  }, []);

  useEffect(() => {
    if (!selectedOs) { setItens([]); setSelectedItem(""); return; }
    api.get(`/itens?os_id=${selectedOs}`).then((r) => setItens(r.data));
    setSelectedItem("");
  }, [selectedOs]);

  const currentItem = itens.find((i) => i.id === selectedItem);
  const falta = currentItem ? Math.max(0, currentItem.qtd_planejada - currentItem.qtd_executada) : 0;
  const pct = currentItem && currentItem.qtd_planejada
    ? Math.min(100, (currentItem.qtd_executada / currentItem.qtd_planejada) * 100)
    : 0;

  // Excesso da quantidade sobre o "falta" (planejado restante)
  const qtdNum = parseFloat(quantidade) || 0;
  const excedente = Math.max(0, qtdNum - falta);
  const currentComp = composicoes.find((c) => c.id === currentItem?.composicao_id);

  // Reset da seção de material extra quando muda item ou quantidade cai
  useEffect(() => {
    if (excedente <= 0) {
      setEnviouMaterialExtra(null);
      setMaterialExtra([]);
    }
  }, [selectedItem, excedente]);

  // Pré-preenche materialExtra com o cálculo teórico do EXCEDENTE
  const preencherExtraTeorico = () => {
    if (!currentComp || excedente <= 0) return;
    const mats = (currentComp.materiais || []).map((m) => ({
      insumo_id: m.insumo_id,
      insumo_nome: m.insumo_nome,
      unidade: m.unidade,
      quantidade: Number((excedente * (m.quantidade_por_unidade || 0)).toFixed(4)),
    }));
    setMaterialExtra(mats);
  };

  const setExtraQtd = (idx, val) => {
    const copy = [...materialExtra];
    copy[idx] = { ...copy[idx], quantidade: parseFloat(val) || 0 };
    setMaterialExtra(copy);
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const compressed = await compressFile(f);
      setFotos((p) => [...p, compressed]);
    }
  };

  const submit = async () => {
    if (!selectedOs || !selectedItem || !quantidade) {
      toast.error("Preencha OS, item e quantidade");
      return;
    }
    if (extras.some((x) => !x.item_id || !(parseFloat(x.quantidade) > 0))) {
      toast.error("Preencha item e quantidade de cada item adicional (ou remova a linha)");
      return;
    }
    // Se há excedente e o usuário ainda não respondeu se enviou material extra
    if (excedente > 0 && enviouMaterialExtra === null) {
      toast.error("Confirme se foi necessário enviar material extra à obra.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        os_id: selectedOs,
        item_id: selectedItem,
        equipe_id: selectedEquipe || null,
        quantidade: parseFloat(quantidade),
        observacao,
        fotos,
        data_execucao: dateWithNowToIso(dataExecucao),
        material_extra: (excedente > 0 && enviouMaterialExtra === true)
          ? materialExtra.filter((m) => (m.quantidade || 0) > 0)
          : [],
        latitude: geo?.latitude ?? null,
        longitude: geo?.longitude ?? null,
        gps_accuracy: null,
        gps_manual: !!geo,
      };
      const extrasValidos = extras.filter((x) => x.item_id && parseFloat(x.quantidade) > 0);
      const payloads = [payload, ...extrasValidos.map((x) => ({ ...payload, item_id: x.item_id, quantidade: parseFloat(x.quantidade), fotos: [], material_extra: [], observacao: `${observacao ? observacao + " · " : ""}Lançado junto com ${currentItem?.descricao || "item principal"}` }))];
      const isNetworkError = (e) => !navigator.onLine || !e?.response || e?.code === "ERR_NETWORK";
      let offline = false;
      for (const pl of payloads) {
        try {
          await api.post("/apontamentos", pl);
        } catch (err) {
          if (isNetworkError(err)) { await enqueue("/apontamentos", pl); offline = true; } else { throw err; }
        }
      }
      if (offline) toast.info(`Sem internet · Apontamento salvo no celular. Vai enviar sozinho quando voltar o sinal.`);
      else toast.success(payloads.length > 1 ? `${payloads.length} apontamentos registrados com sucesso!` : "Apontamento registrado com sucesso!");
      setExtras([]);
      setQuantidade("");
      setObservacao("");
      setFotos([]);
      setEnviouMaterialExtra(null);
      setMaterialExtra([]);
      setGeo(null);
      setDataExecucao(todayISODate());
      // reload items to refresh executed
      const r = await api.get(`/itens?os_id=${selectedOs}`);
      setItens(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(`Erro ao registrar apontamento${detail ? ": " + detail : ""}`);
    } finally {
      setLoading(false);
    }
  };

  if (user && user.role !== "admin") {
    return (
      <div data-testid="apontamento-avulso-bloqueado">
        <PageHeader overline="Módulo" title="Apontamento Avulso" subtitle="Lançamento manual" />
        <div className="pcp-card" style={{ padding: 20, maxWidth: 560 }}>
          <strong>Somente o Administrador faz o apontamento avulso.</strong>
          <div className="text-sm" style={{ color: "#71717A", marginTop: 6 }}>Use os apontamentos de campo: Pintura, Placas, Tachas, Defensas ou Capina/Limpeza.</div>
        </div>
      </div>
    );
  }
  return (
    <>
      <PageHeader
        testid="page-apontamento"
        title="Apontamento Avulso"
        subtitle="Lançamento manual pelo Administrador · coordenada opcional digitada"
      />
      <div className="page-body" style={{ maxWidth: 720, margin: "0 auto", width: "100%" }}>
        <OfflineDataBanner />
        <div className="pcp-card" style={{ padding: 24 }}>
          <div style={{ display: "grid", gap: 20 }}>
            <div>
              <label className="label">Ordem de Serviço</label>
              <select
                className="select input-lg"
                value={selectedOs}
                onChange={(e) => setSelectedOs(e.target.value)}
                data-testid="ap-os"
              >
                <option value="">— Selecione a OS —</option>
                {ordens.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.numero} · {o.contratante} · {o.rodovia}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Item de Serviço</label>
              <select
                className="select input-lg"
                value={selectedItem}
                onChange={(e) => setSelectedItem(e.target.value)}
                disabled={!selectedOs || itens.length === 0}
                data-testid="ap-item"
              >
                <option value="">— Selecione o item —</option>
                {itens.map((i) => (
                  <option key={i.id} value={i.id}>
                    [{i.categoria}] {nomeItem(i)} · resta {(i.qtd_planejada - i.qtd_executada).toFixed(2)} {i.unidade}
                  </option>
                ))}
              </select>
            </div>

            {currentItem && (
              <div style={{ padding: 16, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 3 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div className="overline">Planejado</div>
                    <div className="font-mono font-semibold text-lg">{currentItem.qtd_planejada} {currentItem.unidade}</div>
                  </div>
                  <div>
                    <div className="overline">Executado</div>
                    <div className="font-mono font-semibold text-lg">{currentItem.qtd_executada.toFixed(2)} {currentItem.unidade}</div>
                  </div>
                  <div>
                    <div className="overline">Falta</div>
                    <div className="font-mono font-semibold text-lg" style={{ color: falta === 0 ? "#34C759" : "#DC2626" }}>
                      {falta.toFixed(2)} {currentItem.unidade}
                    </div>
                  </div>
                </div>
                <Progress value={pct} />
              </div>
            )}

            <div>
              <label className="label">Equipe</label>
              <select
                className="select input-lg"
                value={selectedEquipe}
                onChange={(e) => setSelectedEquipe(e.target.value)}
                data-testid="ap-equipe"
              >
                <option value="">— Selecione —</option>
                {equipes.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </div>

            <div>
              <label className="label">
                Data do apontamento
                {dataExecucao !== todayISODate() && (
                  <span
                    data-testid="ap-data-retroativo-badge"
                    style={{
                      marginLeft: 8,
                      padding: "2px 6px",
                      background: "#FFCC00",
                      color: "#0A0A0A",
                      fontSize: "0.68rem",
                      fontWeight: 800,
                      borderRadius: 3,
                      letterSpacing: "0.04em",
                    }}
                  >
                    RETROATIVO
                  </span>
                )}
              </label>
              <input
                type="date"
                className="input input-lg"
                value={dataExecucao}
                max={todayISODate()}
                onChange={(e) => setDataExecucao(e.target.value || todayISODate())}
                data-testid="ap-data"
                style={{ fontSize: "1.1rem", fontWeight: 600 }}
              />
              <div style={{ fontSize: "0.72rem", color: "#71717A", marginTop: 6 }}>
                Padrão: hoje. Altere para registrar produção de um dia anterior.
              </div>
            </div>

            <div>
              <label className="label">Quantidade executada hoje *</label>
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                pattern="[0-9]*"
                className="input input-lg"
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value)}
                placeholder={currentItem ? `Em ${currentItem.unidade}` : "0,00"}
                data-testid="ap-qtd"
                style={{ fontSize: "1.4rem", fontWeight: 600 }}
              />
              {currentItem && quantidade && excedente > 0 && (
                <div style={{ marginTop: 10, padding: 14, background: "#FFFBEA", border: "1px solid #FFCC00", borderRadius: 3 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#7A5D00", fontSize: "0.85rem", fontWeight: 700 }}>
                    <Warning size={16} weight="bold" /> Quantidade acima do planejado restante
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#52525B", marginTop: 4 }}>
                    Você está registrando <strong>{excedente.toFixed(2)} {currentItem.unidade}</strong> a mais do que o previsto para este item.
                    Foi necessário enviar material extra para a obra?
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button
                      type="button"
                      className={`btn btn-sm ${enviouMaterialExtra === true ? "btn-primary" : "btn-outline"}`}
                      onClick={() => { setEnviouMaterialExtra(true); preencherExtraTeorico(); }}
                      data-testid="btn-extra-sim"
                    >Sim, enviei material extra</button>
                    <button
                      type="button"
                      className={`btn btn-sm ${enviouMaterialExtra === false ? "btn-primary" : "btn-outline"}`}
                      onClick={() => { setEnviouMaterialExtra(false); setMaterialExtra([]); }}
                      data-testid="btn-extra-nao"
                    >Não, usei o que já estava lá</button>
                  </div>
                  {enviouMaterialExtra === true && (
                    <div style={{ marginTop: 12, borderTop: "1px dashed #E4B800", paddingTop: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontSize: "0.78rem", fontWeight: 700, color: "#52525B" }}>
                        <Package size={14} weight="bold" /> Ajuste a quantidade real de cada material enviado
                      </div>
                      {(!currentComp || materialExtra.length === 0) ? (
                        <div style={{ fontSize: "0.78rem", color: "#71717A" }}>
                          Este item não tem composição vinculada — não há sugestão automática.
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 8 }}>
                          {materialExtra.map((m, idx) => (
                            <div key={m.insumo_id || idx} style={{ display: "grid", gridTemplateColumns: "1fr 120px 40px", gap: 8, alignItems: "center" }}>
                              <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>{m.insumo_nome}</div>
                              <input
                                type="number" step="0.01" min="0"
                                className="input"
                                value={m.quantidade}
                                onChange={(e) => setExtraQtd(idx, e.target.value)}
                                data-testid={`extra-qtd-${idx}`}
                              />
                              <div style={{ fontSize: "0.78rem", color: "#52525B" }}>{m.unidade}</div>
                            </div>
                          ))}
                          <div style={{ fontSize: "0.72rem", color: "#71717A", marginTop: 4 }}>
                            Os valores foram pré-preenchidos pela composição × excedente. Ajuste para o real. Ao salvar, cada material será
                            debitado do estoque como movimento de saída vinculado a esta OS.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="label">Observações (clima, paralisações, intercorrências)</label>
              <textarea
                className="textarea"
                rows={3}
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Ex: Céu nublado, sem chuva. Sem intercorrências."
                data-testid="ap-obs"
              />
            </div>

            <div>
              <label className="label">Fotos do serviço</label>
              <label
                htmlFor="photo-input"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 8, height: 56, border: "1px dashed #D4D4D8", borderRadius: 3, cursor: "pointer",
                  fontWeight: 600, color: "#0A0A0A", background: "#FAFAFA",
                }}
              >
                <Camera size={20} weight="bold" /> Adicionar fotos
              </label>
              <input
                id="photo-input"
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                style={{ display: "none" }}
                onChange={handlePhotos}
                data-testid="ap-fotos"
              />
              {fotos.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  {fotos.map((f, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      <img src={f} className="photo-thumb" alt="" />
                      <button
                        onClick={() => setFotos(fotos.filter((_, ix) => ix !== i))}
                        style={{
                          position: "absolute", top: -6, right: -6, background: "#0A0A0A", color: "#FFF",
                          borderRadius: "50%", width: 20, height: 20, display: "flex",
                          alignItems: "center", justifyContent: "center", border: 0, cursor: "pointer",
                        }}
                      >
                        <X size={12} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div data-testid="ap-extras">
              <label className="label">Outros itens no mesmo lançamento <span style={{ fontWeight: 400, color: "#71717A", textTransform: "none", letterSpacing: 0 }}>· ex.: terminal/aba junto da defensa</span></label>
              {extras.map((x, i) => {
                const it = itens.find((k) => k.id === x.item_id);
                return (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 120px auto", gap: 8, alignItems: "center", marginBottom: 8 }} data-testid={`ap-extra-${i}`}>
                    <select className="select" value={x.item_id} onChange={(e) => setExtras((p) => p.map((y, j) => (j === i ? { ...y, item_id: e.target.value } : y)))} data-testid={`ap-extra-item-${i}`}>
                      <option value="">— Item —</option>
                      {itens.filter((k) => k.id !== selectedItem && !extras.some((y, j) => j !== i && y.item_id === k.id)).map((k) => (
                        <option key={k.id} value={k.id}>[{k.categoria}] {k.descricao} · falta {Math.max(0, (k.qtd_planejada || 0) - (k.qtd_executada || 0)).toLocaleString("pt-BR")} {k.unidade}</option>
                      ))}
                    </select>
                    <input type="number" step="0.01" inputMode="decimal" className="input" placeholder={it ? `Em ${it.unidade}` : "Qtd"} value={x.quantidade} onChange={(e) => setExtras((p) => p.map((y, j) => (j === i ? { ...y, quantidade: e.target.value } : y)))} data-testid={`ap-extra-qtd-${i}`} />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExtras((p) => p.filter((_, j) => j !== i))} title="Remover" data-testid={`ap-extra-remover-${i}`}><X size={14} /></button>
                  </div>
                );
              })}
              <button type="button" className="btn btn-outline btn-sm" disabled={!selectedOs} onClick={() => setExtras((p) => [...p, { item_id: "", quantidade: "" }])} data-testid="ap-extra-add">+ Adicionar outro item</button>
              {extras.length > 0 && <div className="text-xs" style={{ color: "#71717A", marginTop: 6 }}>Cada item vira um apontamento próprio com a mesma OS, equipe, data, observação e coordenada; as fotos ficam no item principal.</div>}
            </div>

            <div>
              <label className="label">Coordenada do serviço (opcional · digitada)</label>
              <input
                className="input"
                placeholder="Ex: -16.123456, -49.654321 (cole do Google Maps)"
                value={coordTxt}
                onChange={(e) => onCoord(e.target.value)}
                data-testid="coord-manual"
                style={{ borderColor: coordTxt && !geo ? "#DC2626" : geo ? "#059669" : undefined }}
              />
              <div className="text-xs" style={{ marginTop: 4, color: coordTxt && !geo ? "#DC2626" : "#71717A", display: "flex", gap: 8 }} data-testid="coord-manual-status">
                <MapPin size={12} />
                {coordTxt && !geo ? "Formato inválido — use latitude, longitude (ex.: -16.12345, -49.65432)"
                  : geo ? <>Coordenada manual: {geo.latitude.toFixed(6)}, {geo.longitude.toFixed(6)} · <a href={`https://www.google.com/maps?q=${geo.latitude},${geo.longitude}`} target="_blank" rel="noreferrer" style={{ color: "#0A0A0A", textDecoration: "underline" }}>ver no mapa</a></>
                  : "Lançamento manual não usa o GPS deste aparelho (evita gravar a posição do escritório). Se souber a coordenada do serviço, cole aqui."}
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg btn-block"
              onClick={submit}
              disabled={loading || !selectedOs || !selectedItem || !quantidade}
              data-testid="btn-save-apontamento"
            >
              <CheckCircle size={20} weight="bold" /> {loading ? "Registrando…" : "Registrar apontamento"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
